import {
  UNCATEGORIZED,
  formatCategoryLabel,
  getDeviceListId,
  getScreenCategoryFromDeviceId,
} from './screenCategories';

/**
 * Compute the set of "real" location labels for a fleet of devices.
 *
 * Two sources contribute:
 *   1. Site categories derived from device-id structure (e.g. "HO-Floor1").
 *   2. Custom locations admins typed into the setup modal — only counted
 *      when the device is fully set up (profileId + displayName + location),
 *      which filters out auto-seeded garbage like hardware model names.
 *
 * Returned list is alphabetically sorted and contains no duplicates.
 */
export const computeKnownLocations = (devices) => {
  const set = new Set();
  for (const d of devices) {
    const cat = getScreenCategoryFromDeviceId(getDeviceListId(d));
    if (cat && cat !== UNCATEGORIZED) set.add(formatCategoryLabel(cat));
  }
  for (const d of devices) {
    if (d.profileId && d.displayName && d.location) {
      set.add(d.location);
    }
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b));
};

/**
 * Derive the group label for a single device. Prefers the admin-set
 * `location` field (when it matches a known location), else the
 * id-derived site category. Returns null when neither source yields a
 * valid group — caller decides how to render ungrouped devices
 * (ScreensView omits headers; ScreenTimeline buckets them under "Unassigned").
 */
export const getGroupKey = (device, knownLocations) => {
  if (device.location && knownLocations.includes(device.location)) {
    return device.location;
  }
  const cat = getScreenCategoryFromDeviceId(getDeviceListId(device));
  if (cat && cat !== UNCATEGORIZED) return formatCategoryLabel(cat);
  return null;
};

/**
 * Stable comparator: group asc, then displayName asc within group.
 * Pass an `unassignedSentinel` to control where ungrouped devices sort
 * (default empty string sorts them first; pass '￿' to sink them last).
 */
export const compareByGroupThenName = (a, b, knownLocations, unassignedSentinel = '') => {
  const ga = getGroupKey(a, knownLocations) || unassignedSentinel;
  const gb = getGroupKey(b, knownLocations) || unassignedSentinel;
  const cmp = ga.localeCompare(gb);
  if (cmp !== 0) return cmp;
  const la = (a.displayName || a.tvId || a.deviceId || '').toLowerCase();
  const lb = (b.displayName || b.tvId || b.deviceId || '').toLowerCase();
  return la.localeCompare(lb);
};

/**
 * Count grouped devices per label. Ungrouped devices (key=null) are
 * excluded — caller can compute an "Unassigned" count separately if needed.
 */
export const computeCategoryCounts = (devices, knownLocations) => {
  const m = new Map();
  for (const d of devices) {
    const key = getGroupKey(d, knownLocations);
    if (key) m.set(key, (m.get(key) ?? 0) + 1);
  }
  return m;
};
